import React, { useEffect, useState } from "react";
import { styled } from '@mui/material/styles';
import { Box, Container, Grid, Typography, Pagination } from '@mui/material';
import useMediaQuery from "@mui/material/useMediaQuery";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar/Navbar";
import Footer from "../components/Home/Footer";
import BrowserNotSupported from "./BrowserNotSupported";
import ScoreCard from "../components/IELTS/ScoreCard";
import TestCard from "../components/IELTS/TestCard";
import SectionCard from "../components/IELTS/SectionCard";
import SectionalTestCard from "../components/IELTS/SectionalTestCard";
import TestSection from "../components/IELTS/TestSection";
import { 
  CalculatorIcon, 
  TargetIcon, 
  CalendarIcon,
  ListeningIcon,
  ReadingIcon,
  WritingIcon,
  SpeakingIcon
} from "../components/IELTS/icons";
import { 
  testFilters, 
  sections, 
} from "../components/IELTS/mockData";
import { Base_URL } from "../Client/apiURL";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress, TextField, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { getSectionColor, themeColors } from '../components/IELTS/styles/theme';

const isNotChrome = () => {
  const userAgent = navigator.userAgent;
  return !/Chrome/.test(userAgent) || /OPR|Edge/.test(userAgent);
};

const DashboardContainer = styled(Container)(({ theme }) => ({
  paddingTop: '6rem',
  paddingBottom: '2rem',
  [theme.breakpoints.down('md')]: {
    paddingTop: '2rem',
    paddingLeft: '1.5rem',
    paddingRight: '1.5rem',
  },
  [theme.breakpoints.down('sm')]: {
    paddingLeft: '1rem',
    paddingRight: '1rem',
  },
}));

const TestListContainer = styled(Box)({
  minHeight: '500px',
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  flex: 1,
});

const IELTSDashboard = () => {
  const navigate = useNavigate();
  const isLaptopTwo = useMediaQuery("(max-width:1000px)");
  const isMobile = useMediaQuery("(max-width:600px)");
  const [open, setOpen] = React.useState(false);
  const [showFooter, setShowFooter] = React.useState(false);
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedKind, setSelectedKind] = useState(testFilters[0].value);
  const [selectedSection, setSelectedSection] = useState(sections[0].title);
  const [testKinds, setTestKinds] = useState([]);
  const [sectionCounts, setSectionCounts] = useState({
    Listening: 0,
    Reading: 0,
    Writing: 0,
    Speaking: 0
  });
  // IELTS Profile data state
  const [profileData, setProfileData] = useState({
    averageScore: "- / 9",
    targetScore: "- / 9", 
    examIn: "- Days",
    loading: true,
    error: null
  });
  // Pagination states
  const [fullTestPage, setFullTestPage] = useState(1);
  const [sectionalTestPage, setSectionalTestPage] = useState(1);
  const fullTestsPerPage = 6;
  const sectionalTestsPerPage = 5;
  
  // Full test continuation modal states
  const [showContinueModal, setShowContinueModal] = useState(false);
  const [currentTestData, setCurrentTestData] = useState(null);
  const [testCompletionStatus, setTestCompletionStatus] = useState(null);
  const [loadingTestId, setLoadingTestId] = useState(null);
  
  // Change score/date modal states
  const [showTargetScoreModal, setShowTargetScoreModal] = useState(false);
  const [showExamDateModal, setShowExamDateModal] = useState(false);
  const [newTargetScore, setNewTargetScore] = useState('');
  const [newExamDate, setNewExamDate] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  
  // Custom styles for dropdown
  const selectStyles = {
    '& .MuiSelect-select': {
      backgroundColor: '#FFFFFF',
      border: '1px solid #E2E8F0',
      borderRadius: '10px',
      padding: '6px 32px 6px 16px',
      fontSize: '13px',
      fontWeight: 700,
      fontFamily: "'Poppins', 'Inter', sans-serif",
      '&:focus': {
        backgroundColor: '#FFFFFF',
        borderColor: themeColors.primary,
      },
      '&:hover': {
        borderColor: '#CBD5E0',
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '& .MuiSvgIcon-root': {
      color: themeColors.primary,
    },
  };

  // Custom pagination styles
  const paginationStyles = {
    '& .MuiPaginationItem-root': {
      color: '#4A5568',
      fontWeight: 700,
      fontSize: '13px',
      borderRadius: '8px',
      fontFamily: "'Poppins', 'Inter', sans-serif",
      '&.Mui-selected': {
        backgroundColor: themeColors.primary,
        color: 'white',
        '&:hover': {
          backgroundColor: themeColors.primaryHover,
        },
      },
      '&:hover': {
        backgroundColor: '#F7FAFC',
      },
    },
    '& .MuiPaginationItem-ellipsis': {
      color: '#A0AEC0',
    },
    '& .MuiPaginationItem-previousNext': {
      backgroundColor: '#FFFFFF',
      border: '1px solid #E2E8F0',
      '&:hover': {
        backgroundColor: '#F7FAFC',
      },
      '&.Mui-disabled': {
        backgroundColor: '#EDF2F7',
        borderColor: '#E2E8F0',
      },
    },
  };

  // Track selected IELTS test type (general or academic)
  const [selectedTestType, setSelectedTestType] = useState(() => {
    const stored = localStorage.getItem('ielts-type');
    return stored ? stored.toLowerCase() : 'general';
  });

  // Update section counts when tests are loaded or selectedTestType changes
  // Count ALL tests (with or without groupId) for each category
  useEffect(() => {
    if (tests.length > 0) {
      const counts = {
        Listening: 0,
        Reading: 0,
        Writing: 0,
        Speaking: 0
      };
      
      tests.forEach(test => {
        if (test.category_name) {
          // Filter by selectedTestType (academic/general)
          const matchesType = test.test_type ? test.test_type.toLowerCase() === selectedTestType : true;
          
          if (matchesType) {
            // Count all tests including those with groupId
            const category = test.category_name.toLowerCase();
            if (category.includes('listening')) counts.Listening++;
            else if (category.includes('reading')) counts.Reading++;
            else if (category.includes('writing')) counts.Writing++;
            else if (category.includes('speaking')) counts.Speaking++;
          }
        }
      });
      
      setSectionCounts(counts);
    }
  }, [tests, selectedTestType]);

  // Fetch IELTS profile data
  const fetchProfileData = async () => {
    try {
      setProfileData(prev => ({ ...prev, loading: true, error: null }));
      
      const response = await fetch(`${Base_URL}/app/users/get-ielts-profile`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });
      
      const data = await response.json();
      
      if (data.responseCode === 200) {
        const profile = data.response;
        setProfileData({
          averageScore: profile.average_score ? `${profile.average_score} / 9` : "- / 9",
          targetScore: profile.ielts_target_score ? `${profile.ielts_target_score} / 9` : "- / 9",
          examIn: profile.days_until_exam ? `${String(profile.days_until_exam).padStart(2, '0')} Days` : "- Days",
          loading: false,
          error: null
        });
      } else {
        throw new Error(data.message || "Failed to fetch profile data");
      }
    } catch (error) {
      console.error("Error fetching IELTS profile:", error);
      setProfileData(prev => ({
        ...prev,
        loading: false,
        error: error.message
      }));
    }
  };
  
  useEffect(() => {
    setOpen(isNotChrome());
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");
    const portalId = userData?.PortalId || 0;
    setShowFooter(portalId === 1);
    
    // Set exam type to IELTS in localStorage to maintain state
    localStorage.setItem("exam-type", "ielts");
    
    // Set default IELTS type if not already set
    if (!localStorage.getItem("ielts-type")) {
      localStorage.setItem("ielts-type", "general");
    }
    
    // Dispatch a custom event to notify other components about the exam type
    const event = new CustomEvent("examTypeChanged", { detail: { examType: "ielts" } });
    window.dispatchEvent(event);

    // Fetch IELTS tests from API
    const fetchTests = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(
          `${Base_URL}/app/users/ielts-tests`,
          {
            method: "GET",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
        const data = await response.json();
        let testsArr = [];
        if (Array.isArray(data)) {
          testsArr = data;
        } else if (Array.isArray(data.response)) {
          testsArr = data.response;
        }
        setTests(testsArr);
        // Extract unique test_kind values
        const kinds = Array.from(new Set(testsArr.map(t => t.test_kind).filter(Boolean)));
        setTestKinds(kinds);
        
      } catch (err) {
        setError("Failed to fetch IELTS tests");
        setTests([]);
        setTestKinds([]);
      } finally {
        setLoading(false);
      }
    };
    fetchTests();
    fetchProfileData();

    // Listen for IELTS type changes from the toggle component
    const handleIeltsTypeChange = (e) => {
      const { ieltsType } = e.detail || {};
      if (ieltsType) {
        setSelectedTestType(ieltsType.toLowerCase());
      }
    };
    window.addEventListener('ieltsTypeChanged', handleIeltsTypeChange);

    // Cleanup listener on unmount
    return () => {
      window.removeEventListener('ieltsTypeChanged', handleIeltsTypeChange);
    };
  }, []);

  const handleChangeTargetScore = () => {
    setShowTargetScoreModal(true);
  };

  const handleChangeExamDate = () => {
    setShowExamDateModal(true);
  };

  const updateTargetScore = async () => {
    if (!newTargetScore) return;
    
    try {
      setIsUpdating(true);
      
      const response = await fetch(`${Base_URL}/app/users/update-ielts-profile`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ielts_target_score: parseFloat(newTargetScore)
        })
      });
      
      const data = await response.json();
      
      if (data.responseCode === 200) {
        // Refresh profile data after successful update
        await fetchProfileData();
        setShowTargetScoreModal(false);
        setNewTargetScore('');
      } else {
        console.error("Failed to update target score:", data.message);
        alert("Failed to update target score. Please try again.");
      }
    } catch (error) {
      console.error("Error updating target score:", error);
      alert("Error updating target score. Please try again.");
    } finally {
      setIsUpdating(false);
    }
  };

  const updateExamDate = async () => {
    if (!newExamDate) return;
    
    try {
      setIsUpdating(true);
      
      const response = await fetch(`${Base_URL}/app/users/update-ielts-profile`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ielts_exam_date: newExamDate
        })
      });
      
      const data = await response.json();
      
      if (data.responseCode === 200) {
        // Refresh profile data after successful update
        await fetchProfileData();
        setShowExamDateModal(false);
        setNewExamDate('');
      } else {
        console.error("Failed to update exam date:", data.message);
        alert("Failed to update exam date. Please try again.");
      }
    } catch (error) {
      console.error("Error updating exam date:", error);
      alert("Error updating exam date. Please try again.");
    } finally {
      setIsUpdating(false);
    }
  };

  const checkFullTestStatus = async (groupId) => {
    try {
      const response = await fetch(
        `${Base_URL}/app/users/ielts-tests/checkFullTestCompletionStatus?groupId=${groupId}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      const data = await response.json();
      if (data.responseCode === 200) {
        return data.response.status;
      }
      return null;
    } catch (error) {
      console.error("Error checking full test status:", error);
      return null;
    }
  };

  const handleStartTest = async (testId, mode, isFullTest = false, loadingId = null) => {
    console.log(`Starting test ${testId} in ${mode || 'default'} mode, isFullTest: ${isFullTest}`);
    
    // Use provided loadingId or generate one based on test type
    const uniqueLoadingId = loadingId || (isFullTest ? `full-${testId}` : `sectional-${testId}`);
    
    if (isFullTest) {
      setLoadingTestId(uniqueLoadingId);
      try {
        // For full tests, find all tests with the same groupId
        const test = tests.find(t => t.id === testId);
        if (test && test.groupId) {
          const groupedTests = tests.filter(t => t.groupId === test.groupId);
          
          // Check if user has an incomplete test
          const status = await checkFullTestStatus(test.groupId);
          
          if (status && status.hasFullTestAttempt && !status.isCompleted) {
            // Show continue/restart modal
            setCurrentTestData({
              testId,
              mode: mode || 'practice',
              groupedTests,
              groupId: test.groupId
            });
            setTestCompletionStatus(status);
            setShowContinueModal(true);
          } else {
            // Navigate directly to full test page
            navigate('/ielts-full-test', {
              state: {
                mode: mode || 'practice',
                groupedTests: groupedTests,
                groupId: test.groupId
              }
            });
          }
        }
      } finally {
        setLoadingTestId(null);
      }
    } else {
      // For sectional tests (with or without groupId), fetch test details
      // Tests with groupId can be attempted individually as sectional tests
      setLoadingTestId(uniqueLoadingId);
      try {
        const response = await fetch(
          `${Base_URL}/app/users/ielts-tests/getTestById/${testId}`,
          {
            method: "GET",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        const test = data.response;
        
        if (!test) {
          throw new Error("Test not found");
        }
        
        if (test.category_name === 'Listening') {
          navigate('/ielts-listening-test', { 
            state: { 
              duration: 30, // Default duration for listening test (IELTS Listening is 30 min)
              mode: mode || 'practice',
              testData: test // Pass the full test object with sections
            } 
          });
        } else if (test.category_name === 'Writing') {
          navigate('/ielts-writing-test', { 
            state: { 
              duration: 60, // Default duration for writing test
              mode: mode || 'practice',
              testData: test // Pass the full test object with sections
            } 
          });
        } else if (test.category_name === 'Reading') {
          navigate('/ielts-reading-test', { 
            state: { 
              duration: 60, // Default duration for reading test
              mode: mode || 'practice',
              testData: test // Pass the full test object with sections
            } 
          });
        } else if (test.category_name === 'Speaking') {
          const userData = JSON.parse(localStorage.getItem('userData') || '{}');
          const currentUserId = userData?.UserId || 1;
          navigate('/ielts-speaking-test', {
            state: {
              duration: 15, // Default duration for speaking test
              mode: mode || 'practice',
              testData: test, // Pass the full test object with sections
              currentUserId
            }
          });
        }
      } catch (error) {
        console.error("Error fetching test details:", error);
        alert("Failed to load test details. Please try again.");
      } finally {
        setLoadingTestId(null);
      }
    }
  };

  const handleContinueTest = () => {
    if (currentTestData) {
      navigate('/ielts-full-test', {
        state: {
          mode: currentTestData.mode,
          groupedTests: currentTestData.groupedTests,
          groupId: currentTestData.groupId,
          continuePrevious: true,
          completionStatus: testCompletionStatus
        }
      });
    }
    setShowContinueModal(false);
  };

  const handleRestartTest = () => {
    if (currentTestData) {
      navigate('/ielts-full-test', {
        state: {
          mode: currentTestData.mode,
          groupedTests: currentTestData.groupedTests,
          groupId: currentTestData.groupId,
          continuePrevious: false
        }
      });
    }
    setShowContinueModal(false);
  };

  const handleViewAllFullTests = () => {
    console.log("View all full tests");
  };

  const handleViewAllSectionalTests = () => {
    console.log("View all sectional tests");
  };

  // Map dropdown value to test_kind for filtering
  const kindMap = {
    "Cambridge": "cambridge",
    "British Council": "british council",
    "IDP": "idp"
  };

  // Group tests by groupId for Full Test section
  const getFullTestGroups = () => {
    const groupedTests = {};
    tests.forEach(test => {
      if (test.groupId) {
        if (!groupedTests[test.groupId]) {
          groupedTests[test.groupId] = [];
        }
        groupedTests[test.groupId].push(test);
      }
    });

    // Convert to array and filter by selected kind and test type
    // Preserve backend order by using the order tests appear in the original array
    const groupOrder = new Map(); // Track order of first appearance of each group
    tests.forEach((test, index) => {
      if (test.groupId && !groupOrder.has(test.groupId)) {
        groupOrder.set(test.groupId, index);
      }
    });

    const fullTestGroups = Object.entries(groupedTests)
      .map(([groupId, groupTests]) => {
        // Check if any test in the group matches the selected kind AND test type
        // Always check both kind and type, even when "All" is selected for kind
        const hasMatchingFilters = groupTests.some(test => {
          // Match by test_kind (Cambridge, British Council, IDP)
          const matchesKind = selectedKind === 'All' ? true : (test.test_kind ? test.test_kind.toLowerCase() === (kindMap[selectedKind] || selectedKind.toLowerCase()) : false);
          // Match by test type (academic/general) - always check this
          const matchesType = test.test_type ? test.test_type.toLowerCase() === selectedTestType : true;
          return matchesKind && matchesType;
        });
        
        if (hasMatchingFilters) {
          // Create a representative test object for the group
          const representativeTest = groupTests[0]; // Use first test as representative
          const allCategories = [...new Set(groupTests.map(t => t.category_name))].filter(Boolean);
          const totalDuration = groupTests.reduce((sum, test) => sum + (test.total_time || 0), 0);
          
          // Check if all required categories are present
          const requiredCategories = ['Listening', 'Reading', 'Writing'];
          const categoryNamesLower = allCategories.map(cat => cat.toLowerCase());
          const hasAllRequiredCategories = requiredCategories.every(cat => 
            categoryNamesLower.includes(cat.toLowerCase())
          );
          const missingCategories = requiredCategories.filter(cat => 
            !categoryNamesLower.includes(cat.toLowerCase())
          );
          
          // Get group_name from any test in the group (they should all have the same group_name)
          const groupName = representativeTest.group_name || groupTests.find(t => t.group_name)?.group_name;
          
          // Format test name: use group_name if available, otherwise use default format
          let testName = `Complete IELTS Test ${representativeTest.groupId}`;
          if (groupName) {
            testName = groupName;
          }
          
          return {
            id: representativeTest.id,
            groupId: representativeTest.groupId,
            test_name: testName,
            group_name: groupName,
            test_type: representativeTest.test_type,
            test_kind: representativeTest.test_kind || 'Cambridge',
            total_time: totalDuration > 0 ? totalDuration : 165, // Default 2h 45m (30+60+60+15)
            categories: allCategories,
            testsInGroup: groupTests.length,
            groupTests: groupTests,
            hasAllRequiredCategories,
            missingCategories,
            _order: groupOrder.get(parseInt(groupId)) || 0
          };
        }
        return null;
      })
      .filter(Boolean)
      // Sort by original backend order (preserve order from tests array)
      .sort((a, b) => a._order - b._order);

    return fullTestGroups;
  };

  const fullTests = getFullTestGroups();
  
  // Get user subscription status
  const userData = JSON.parse(localStorage.getItem("userData") || "{}");
  const isSubscribed = userData?.IsSubscribed || false;
  
  // Add isUnlocked property to full tests (only first test unlocked if not subscribed)
  const fullTestsWithLock = fullTests.map((test, index) => ({
    ...test,
    isUnlocked: isSubscribed ? true : index === 0
  }));
  
  // Pagination for Full Tests
  const fullTestsStartIndex = (fullTestPage - 1) * fullTestsPerPage;
  const paginatedFullTests = fullTestsWithLock.slice(fullTestsStartIndex, fullTestsStartIndex + fullTestsPerPage);
  const totalFullTestPages = Math.ceil(fullTestsWithLock.length / fullTestsPerPage);

  // Filter tests by selectedSection for Sectional Test section
  // Include ALL tests (with or without groupId) that match the selected section
  // Tests with groupId are part of a full test but can also be attempted individually
  // Note: Tests are already sorted by backend (by root test's created_at), so we preserve that order
  const sectionalTests = tests.filter(test => {
    // Match by category name (Listening, Reading, Writing, Speaking)
    const matchesSection = test.category_name && test.category_name.toLowerCase() === selectedSection.toLowerCase();
    // Match by test type (academic/general) - default to true if test_type is not set
    const matchesType = test.test_type ? test.test_type.toLowerCase() === selectedTestType : true;
    // Match by test_kind (Cambridge, British Council, IDP) - if "All" is selected, match all kinds
    const matchesKind = selectedKind === 'All' ? true : (test.test_kind ? test.test_kind.toLowerCase() === (kindMap[selectedKind] || selectedKind.toLowerCase()) : false);
    // Return true if all conditions match (tests with groupId are included)
    return matchesSection && matchesType && matchesKind;
  });

  // Add isUnlocked property to sectional tests (only first test unlocked per section if not subscribed)
  const sectionalTestsWithLock = sectionalTests.map((test, index) => ({
    ...test,
    isUnlocked: isSubscribed ? true : index === 0
  }));
  
  // Pagination for Sectional Tests
  const sectionalTestsStartIndex = (sectionalTestPage - 1) * sectionalTestsPerPage;
  const paginatedSectionalTests = sectionalTestsWithLock.slice(sectionalTestsStartIndex, sectionalTestsStartIndex + sectionalTestsPerPage);
  const totalSectionalTestPages = Math.ceil(sectionalTestsWithLock.length / sectionalTestsPerPage);

  // Dropdown change handler
  const handleFilterChange = (value) => {
    setSelectedKind(value);
  };

  // Section tab click handler
  const handleSectionTabClick = (sectionTitle) => {
    setSelectedSection(sectionTitle);
    // Removed navigation for Reading and Writing to show tests here
  };

  // Helper function to get the appropriate icon for a section
  // Note: Color is now handled by SectionCard component
  const getSectionIcon = (iconName) => {
    switch (iconName) {
      case 'listening':
        return <ListeningIcon sx={{ fontSize: 24 }} />;
      case 'reading':
        return <ReadingIcon sx={{ fontSize: 24 }} />;
      case 'writing':
        return <WritingIcon sx={{ fontSize: 24 }} />;
      case 'speaking':
        return <SpeakingIcon sx={{ fontSize: 24 }} />;
      default:
        return null;
    }
  };

  // Render the Full Test section
  const renderFullTestSection = () => (
    <Box sx={{ width: '100%' }}>
      <TestSection 
        title="Full Test" 
        filter={{
          options: testFilters,
          defaultValue: testFilters[0].value,
          value: selectedKind,
          onChange: (e) => handleFilterChange(e.target ? e.target.value : e),
          sx: selectStyles
        }}
        onViewAll={handleViewAllFullTests}
      >
        <TestListContainer>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
              <CircularProgress size={30} sx={{ color: themeColors.primary }} />
            </Box>
          ) : error ? (
            <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
              <Typography color="error">{error}</Typography>
            </Box>
          ) : paginatedFullTests.length === 0 ? (
            <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
              <Typography color="textSecondary">No tests available.</Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ flex: 1 }}>
                {paginatedFullTests.map(test => {
                  const isMissingCategories = !test.hasAllRequiredCategories;
                  const disabledMessage = isMissingCategories 
                    ? `Full test is under development. ${test.missingCategories?.join(', ')} sections are being added.`
                    : null;
                  
                  return (
                    <TestCard 
                      key={`group-${test.groupId}`}
                      title={test.test_name}
                      time={test.total_time ? `${Math.floor(test.total_time / 60)}h:${String(test.total_time % 60).padStart(2, '0')}m` : '2h:30m'}
                      difficulty={test.test_type || 'N/A'}
                      onStart={() => {
                        const loadingId = test.groupId ? `full-${test.groupId}` : `full-${test.id}`;
                        handleStartTest(test.id, 'practice', true, loadingId);
                      }}
                      loading={loadingTestId === (test.groupId ? `full-${test.groupId}` : `full-${test.id}`)}
                      isLocked={!test.isUnlocked}
                      disabledMessage={disabledMessage}
                    />
                  );
                })}
              </Box>
              {totalFullTestPages > 1 && (
                <Box display="flex" justifyContent="center" mt="auto" pt={3} mb={1}>
                  <Pagination 
                    count={totalFullTestPages} 
                    page={fullTestPage} 
                    onChange={(e, page) => setFullTestPage(page)}
                    color="primary"
                    size="medium"
                    shape="rounded"
                    siblingCount={1}
                    boundaryCount={1}
                    sx={paginationStyles}
                  />
                </Box>
              )}
            </>
          )}
        </TestListContainer>
      </TestSection>
    </Box>
  );

  // Render the Sectional Test section
  const renderSectionalTestSection = () => (
    <Box sx={{ width: '100%' }}>
      <TestSection 
        title="Sectional Test" 
        onViewAll={handleViewAllSectionalTests}
      >
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {sections.map(section => (
            <Grid item xs={6} sm={3} key={section.id}>
              <SectionCard 
                title={section.title}
                count={sectionCounts[section.title] || 0}
                icon={getSectionIcon(section.icon)}
                onClick={() => handleSectionTabClick(section.title)}
                isSelected={selectedSection === section.title}
              />
            </Grid>
          ))}
        </Grid>
        <TestListContainer>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
              <CircularProgress size={30} sx={{ color: themeColors.primary }} />
            </Box>
          ) : error ? (
            <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
              <Typography color="error">{error}</Typography>
            </Box>
          ) : paginatedSectionalTests.length === 0 ? (
            <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
              <Typography color="textSecondary">No sectional tests available.</Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ flex: 1 }}>
                {paginatedSectionalTests.map(test => {
                  const testName = test.test_name || `Test ${test.id}`;
                  return (
                    <SectionalTestCard 
                      key={test.id}
                      title={testName}
                      time={test.total_time ? `${Math.floor(test.total_time / 60)}h:${String(test.total_time % 60).padStart(2, '0')}m` : (() => {
                        const testNameLower = testName.toLowerCase();
                        if (testNameLower.includes('reading')) return '01:00h';
                        if (testNameLower.includes('writing')) return '01:00h';
                        if (testNameLower.includes('speaking')) return '00:15m';
                        if (testNameLower.includes('listening')) return '00:30m';
                        return '00:30m';
                      })()}
                      difficulty={test.test_type || 'N/A'}
                      onStart={(mode) => {
                        // For sectional tests, use unique identifier
                        const loadingId = `sectional-${test.id}`;
                        handleStartTest(test.id, mode, false, loadingId);
                      }}
                      testType={testName.split('-').pop().trim()}
                      selectedSection={selectedSection}
                      isLocked={!test.isUnlocked}
                      loading={loadingTestId === `sectional-${test.id}`}
                    />
                  );
                })}
              </Box>
              {totalSectionalTestPages > 1 && (
                <Box display="flex" justifyContent="center" mt="auto" pt={3} mb={1}>
                  <Pagination 
                    count={totalSectionalTestPages} 
                    page={sectionalTestPage} 
                    onChange={(e, page) => setSectionalTestPage(page)}
                    color="primary"
                    size="medium"
                    shape="rounded"
                    siblingCount={1}
                    boundaryCount={1}
                    sx={paginationStyles}
                  />
                </Box>
              )}
            </>
          )}
        </TestListContainer>
      </TestSection>
    </Box>
  );

  // Render continue/restart modal
  const renderContinueModal = () => (
    <Dialog
      open={showContinueModal}
      onClose={() => setShowContinueModal(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          padding: '8px'
        }
      }}
    >
      <DialogTitle sx={{ 
        textAlign: 'center', 
        color: themeColors.primary, 
        fontWeight: 600,
        fontSize: '24px',
        pb: 1
      }}>
        Continue or Restart Test?
      </DialogTitle>
      
      <DialogContent sx={{ pb: 1 }}>
        <Typography variant="body1" color="text.secondary" paragraph sx={{ textAlign: 'center', mb: 3 }}>
          Your last attempt was not completed. Would you like to continue from where you left off or restart from the beginning?
        </Typography>
        
        {testCompletionStatus && (
          <Box sx={{ backgroundColor: '#F8F9FA', p: 2, borderRadius: '8px', mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Completed Sections:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {Object.entries(testCompletionStatus.attempted).map(([section, completed]) => (
                <Box
                  key={section}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 2,
                    py: 0.5,
                    borderRadius: '16px',
                    backgroundColor: completed ? getSectionColor('Writing').lighter : getSectionColor('Reading').lighter,
                    border: `1px solid ${completed ? getSectionColor('Writing').primary : getSectionColor('Reading').primary}`
                  }}
                >
                  <Typography variant="body2" sx={{ 
                    color: completed ? getSectionColor('Writing').primary : getSectionColor('Reading').hover,
                    fontWeight: 600,
                    textTransform: 'capitalize'
                  }}>
                    {section}
                  </Typography>
                  <Typography variant="body2" sx={{ color: completed ? getSectionColor('Writing').primary : getSectionColor('Reading').hover }}>
                    {completed ? '✓' : '○'}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'center', gap: 2 }}>
        <Button
          onClick={handleContinueTest}
          variant="contained"
          sx={{ 
            backgroundColor: getSectionColor('Writing').primary,
            '&:hover': { backgroundColor: getSectionColor('Writing').hover },
            px: 3
          }}
        >
          Continue
        </Button>
        <Button
          onClick={handleRestartTest}
          variant="outlined"
          sx={{ 
            borderColor: themeColors.primary,
            color: themeColors.primary,
            '&:hover': { 
              borderColor: themeColors.primaryHover,
              backgroundColor: themeColors.primaryLighter
            },
            px: 3
          }}
        >
          Restart
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Render target score modal
  const renderTargetScoreModal = () => (
    <Dialog
      open={showTargetScoreModal}
      onClose={() => setShowTargetScoreModal(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          padding: '8px'
        }
      }}
    >
      <DialogTitle sx={{ 
        textAlign: 'center', 
        color: themeColors.primary, 
        fontWeight: 600,
        fontSize: '24px',
        pb: 1
      }}>
        Update Target Score
      </DialogTitle>
      
      <DialogContent sx={{ pb: 1 }}>
        <Typography variant="body1" color="text.secondary" paragraph sx={{ textAlign: 'center', mb: 3 }}>
          Set your IELTS target score (0.0 - 9.0)
        </Typography>
        
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Target Score</InputLabel>
          <Select
            value={newTargetScore}
            label="Target Score"
            onChange={(e) => setNewTargetScore(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
              }
            }}
          >
            {[4.0, 4.5, 5.0, 5.5, 6.0, 6.5, 7.0, 7.5, 8.0, 8.5, 9.0].map((score) => (
              <MenuItem key={score} value={score}>
                {score.toFixed(1)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'center', gap: 2 }}>
        <Button
          onClick={updateTargetScore}
          variant="contained"
          disabled={!newTargetScore || isUpdating}
          sx={{ 
            backgroundColor: themeColors.primary,
            '&:hover': { backgroundColor: themeColors.primaryHover },
            px: 3
          }}
        >
          {isUpdating ? <CircularProgress size={20} color="inherit" /> : 'Update'}
        </Button>
        <Button
          onClick={() => {
            setShowTargetScoreModal(false);
            setNewTargetScore('');
          }}
          variant="outlined"
          disabled={isUpdating}
          sx={{ 
            borderColor: themeColors.primary,
            color: themeColors.primary,
            '&:hover': { 
              borderColor: themeColors.primaryHover,
              backgroundColor: themeColors.primaryLighter
            },
            px: 3
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Render exam date modal
  const renderExamDateModal = () => (
    <Dialog
      open={showExamDateModal}
      onClose={() => setShowExamDateModal(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          padding: '8px'
        }
      }}
    >
      <DialogTitle sx={{ 
        textAlign: 'center', 
        color: themeColors.primary, 
        fontWeight: 600,
        fontSize: '24px',
        pb: 1
      }}>
        Update Exam Date
      </DialogTitle>
      
      <DialogContent sx={{ pb: 1 }}>
        <Typography variant="body1" color="text.secondary" paragraph sx={{ textAlign: 'center', mb: 3 }}>
          Set your IELTS exam date
        </Typography>
        
        <TextField
          label="Exam Date"
          type="date"
          value={newExamDate}
          onChange={(e) => setNewExamDate(e.target.value)}
          fullWidth
          InputLabelProps={{
            shrink: true,
          }}
          inputProps={{
            min: new Date().toISOString().split('T')[0], // Today's date as minimum
          }}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
            }
          }}
        />
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'center', gap: 2 }}>
        <Button
          onClick={updateExamDate}
          variant="contained"
          disabled={!newExamDate || isUpdating}
          sx={{ 
            backgroundColor: themeColors.primary,
            '&:hover': { backgroundColor: themeColors.primaryHover },
            px: 3
          }}
        >
          {isUpdating ? <CircularProgress size={20} color="inherit" /> : 'Update'}
        </Button>
        <Button
          onClick={() => {
            setShowExamDateModal(false);
            setNewExamDate('');
          }}
          variant="outlined"
          disabled={isUpdating}
          sx={{ 
            borderColor: themeColors.primary,
            color: themeColors.primary,
            '&:hover': { 
              borderColor: themeColors.primaryHover,
              backgroundColor: themeColors.primaryLighter
            },
            px: 3
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <div className="dashboard-root-flex">
      <BrowserNotSupported open={open} close={setOpen} />
      <Navbar />
      <div className="dashboard-content-flex">
      <DashboardContainer maxWidth="lg">
        {/* Score Cards */}
        <Grid container spacing={4} sx={{ mt: { xs: 2, md: 3 }, mb: { xs: 2, md: 4 } }}>
          <Grid item xs={12} sm={4}>
            <ScoreCard 
              title="Average Score" 
              score={profileData.loading ? "Loading..." : profileData.error ? "Error" : profileData.averageScore} 
              icon={<CalculatorIcon sx={{ fontSize: 28, color: '#FF6B6B' }} />}
              bgcolor="#FFEAEA"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <ScoreCard 
              title="Target Score" 
              score={profileData.loading ? "Loading..." : profileData.error ? "Error" : profileData.targetScore} 
              icon={<TargetIcon sx={{ fontSize: 28, color: themeColors.primary }} />}
              bgcolor={themeColors.primaryLighter}
              showChangeButton
              onChangeClick={handleChangeTargetScore}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <ScoreCard 
              title="Exam In" 
              score={profileData.loading ? "Loading..." : profileData.error ? "Error" : profileData.examIn} 
              icon={<CalendarIcon sx={{ fontSize: 28, color: '#00B4D8' }} />}
              bgcolor="#E1F9FF"
              showChangeButton
              onChangeClick={handleChangeExamDate}
            />
          </Grid>
        </Grid>

        {/* Test Sections - Side by side on larger screens, stacked on mobile */}
        {isMobile ? (
          <Box sx={{ mt: 3 }}>
            {renderFullTestSection()}
            <Box sx={{ height: '32px' }} />
            {renderSectionalTestSection()}
          </Box>
        ) : (
          <Grid container spacing={4} sx={{ mt: 4, mb: 4 }}>
            <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
              {renderFullTestSection()}
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
              {renderSectionalTestSection()}
            </Grid>
          </Grid>
        )}
      </DashboardContainer>
      </div>
      {showFooter && (
        <Box sx={{ padding: "0rem 3% 0rem", background: "white" }}>
          <Footer />
        </Box>
      )}
      {renderContinueModal()}
      {renderTargetScoreModal()}
      {renderExamDateModal()}
    </div>
  );
};

export default IELTSDashboard; 